// Defined here (a persistent <script src>) rather than in the swappable
// snippets/cart-drawer.liquid markup itself — CartDrawer.renderContents()
// below replaces #CartDrawer's innerHTML on every cart change, and a
// <script> tag inserted that way never executes. The buttons still call
// this via a plain onclick="" attribute (see snippets/cart-drawer.liquid),
// which keeps working across re-renders since it's just an attribute
// reference to a function living in an untouched, always-loaded file.
function selectDrawerCause(btn) {
  document.querySelectorAll('.wdp-cart-drawer__cause-option').forEach(function (b) {
    b.classList.remove('wdp-cart-drawer__cause-option--selected');
  });
  btn.classList.add('wdp-cart-drawer__cause-option--selected');
  var cause = btn.dataset.cause;
  var label = document.getElementById('CartDrawer-CauseSelected');
  var name = document.getElementById('CartDrawer-CauseName');
  if (label) label.hidden = false;
  if (name) name.textContent = cause;
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { '1MJ Cause': cause } }),
  });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Always start a freshly-opened drawer scrolled to its own top — the
    // drawer now often carries enough content (upsells, the 1MJ cause
    // selector, footer) to scroll internally, and nothing should carry
    // over a stale scroll position from a previous open.
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.scrollTop = 0;

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    // Mobile Safari renders `position: fixed` descendants relative to the
    // page's scrolled position (not the visual viewport) while the body is
    // merely `overflow: hidden` — so opening the drawer after scrolling
    // down the page (e.g. adding to cart from partway down a collection
    // grid) visually shifts the whole drawer up by that scroll amount,
    // pushing its top content (the line items) off-screen above the fold
    // even though the drawer itself reports scrollTop: 0. Locking the body
    // to `position: fixed` at its current scroll offset — the standard
    // iOS scroll-lock technique — keeps it visually anchored so the fixed
    // drawer renders relative to the real viewport instead.
    this.scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.style.top = `-${this.scrollY}px`;
    document.body.classList.add('overflow-hidden', 'wdp-scroll-locked');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'wdp-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollY || 0);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // The swap above only replaces #CartDrawer's *contents*, so this
    // element's own `is-empty` class (set server-side once, on initial page
    // load) never gets cleared here — leaving a stale empty-state layout
    // after a customer's first add-to-cart. Sync it from the section HTML
    // Shopify just rendered fresh.
    const freshCartDrawer = this.getSectionDOM(parsedState.sections['cart-drawer'], 'cart-drawer');
    if (freshCartDrawer) {
      this.classList.toggle('is-empty', freshCartDrawer.classList.contains('is-empty'));

      // Keep the "1MJ Total Jars" order attribute in sync from here too —
      // now that checkout can happen straight from the drawer without ever
      // touching the /cart page (see sections/wdp-cart.liquid, which only
      // covers that page), this is the only reliable place left that fires
      // on every cart change. Read the jar count Liquid already computed
      // (product.metafields.custom.jar_count-aware) from the fresh render
      // rather than recomputing it in JS, which has no metafield access.
      const jarSync = freshCartDrawer.querySelector('#CartDrawer-JarSync');
      if (jarSync && jarSync.dataset.cartHasItems === 'true') {
        const attrs = { '1MJ Total Jars': jarSync.dataset.totalJars };
        // First item added and no cause chosen yet: save the pre-selected
        // cause so the order carries it without the customer touching anything.
        if (jarSync.dataset.defaultCause && !jarSync.dataset.currentCause) {
          attrs['1MJ Cause'] = jarSync.dataset.defaultCause;
        }
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs }),
        });
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
